// src/composables/useCalendarEvents.js
import { ref } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'https://calendar-navy-eight.vercel.app';

export function useCalendarEvents() {
  const events = ref([]);
  const loading = ref(false);
  const error = ref(null);

  const { getAccessTokenSilently } = useAuth0();

  // Helper function to build headers with Auth0 Access Token
  const getAuthHeaders = async () => {
    const token = await getAccessTokenSilently();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // 1. GET /api/events
  const fetchEvents = async () => {
    loading.value = true;
    error.value = null;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/events`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}:${response.statusText}`);
      }

      events.value = await response.json();
    } catch (err) {
      console.error('Failed to fetch events:', err);
      error.value = err.message || 'Failed to load events';
    } finally {
      loading.value = false;
    }
  };

  // 2. POST /api/events
  const addEvent = async (eventData) => {
    loading.value = true;
    error.value = null;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/events`, {
        method: 'POST',
        headers,
        body: JSON.stringify(eventData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}:${response.statusText}`);
      }

      const createdEvent = await response.json();
      
      // Update local reactive list immediately
      events.value.push(createdEvent);
      return createdEvent;
    } catch (err) {
      console.error('Failed to create event:', err);
      error.value = err.message || 'Failed to save event';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // 3. PUT /api/events (Update Existing Event)
  const updateEvent = async (id, updatedData) => {
    loading.value = true;
    error.value = null;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/events?id=${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}:${response.statusText}`);
      }

      // Read response or construct updated object
      let updatedEvent;
      try {
        updatedEvent = await response.json();
      } catch {
        updatedEvent = { id, ...updatedData };
      }

      // Update the event locally in the reactive array so UI updates instantly
      const index = events.value.findIndex(e => e.id === id);
      if (index !== -1) {
        events.value[index] = { ...events.value[index], ...updatedEvent };
      }

      return updatedEvent;
    } catch (err) {
      console.error('Failed to update event:', err);
      error.value = err.message || 'Failed to update event';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // 4. DELETE /api/events (Delete Event)
  const deleteEvent = async (id) => {
    loading.value = true;
    error.value = null;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/events?id=${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}:${response.statusText}`);
      }

      // Remove event from local reactive array
      events.value = events.value.filter(e => e.id !== id);
    } catch (err) {
      console.error('Failed to delete event:', err);
      error.value = err.message || 'Failed to delete event';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    events,
    loading,
    error,
    fetchEvents,
    addEvent,
    updateEvent,
    deleteEvent,
  };
}